import React, { useState, useRef, useEffect } from "react";
import { categories, transmissions, fuels, features as allFeatures } from "../mock";
import { locationsApi } from "../api";
import { Camera, X, Star, Trash2 } from "lucide-react";

const defaultCar = {
  name: "",
  brand: "",
  model: "",
  year: 2024,
  seats: 5,
  bags: 2,
  category: "Sedan",
  price: 0,
  transmission: "Automatic",
  fuel: "Gasoline",
  description: "",
  unlimitedMileage: true,
  mileageCap: "",
  minDriverAge: 21,
  features: [],
  pickupLocation: "Santo Domingo",
  dropoffLocation: "Santo Domingo",
  images: [],
};

export default function CarFormModal({ initial, onClose, onSave }) {
  const fileInputRef = useRef(null);
  const [locations, setLocations] = useState([]);
  useEffect(() => {
    locationsApi.list().then(setLocations).catch(() => setLocations([]));
  }, []);
  const [form, setForm] = useState(() => {
    const init = initial || {};
    // Backward compat: if old `image` field exists, seed images array
    const seedImages = Array.isArray(init.images) && init.images.length > 0
      ? init.images
      : init.image ? [init.image] : [];
    return {
      ...defaultCar,
      ...init,
      features: init.features || [],
      images: seedImages,
      location: init.location || "Santo Domingo",
      pickupLocation: init.pickupLocation || init.location || "Santo Domingo",
      dropoffLocation: init.dropoffLocation || init.location || "Santo Domingo",
    };
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleFeature = (f) =>
    setForm((s) => ({
      ...s,
      features: s.features.includes(f) ? s.features.filter((x) => x !== f) : [...s.features, f],
    }));

  const handleFiles = (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    if (files.length === 0) return;
    Promise.all(
      files.map(
        (file) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          })
      )
    ).then((urls) => {
      setForm((s) => ({ ...s, images: [...s.images, ...urls] }));
    });
  };

  const onPickFiles = (e) => {
    handleFiles(e.target.files);
    e.target.value = ""; // allow re-selecting same file
  };

  const removeImage = (idx) =>
    setForm((s) => ({ ...s, images: s.images.filter((_, i) => i !== idx) }));

  const makeMain = (idx) =>
    setForm((s) => {
      if (idx === 0) return s;
      const copy = [...s.images];
      const [pick] = copy.splice(idx, 1);
      copy.unshift(pick);
      return { ...s, images: copy };
    });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.brand || !form.price) return;
    onSave({
      ...form,
      location: form.pickupLocation,
      image: form.images[0] || "", // keep legacy field for list thumbnails
    });
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-5">
          <h2 className="text-2xl font-extrabold">{initial ? "Edit Vehicle" : "Add Vehicle"}</h2>
          <button className="text-gray-400 hover:text-gray-700" onClick={onClose}><X size={22} /></button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="field-label">Vehicle Photos (first photo is the main image)</label>
            {form.images.length === 0 ? (
              <div className="photo-empty">
                <span>No photos yet — click "Add Photo" below</span>
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {form.images.map((src, idx) => (
                  <div key={idx} className="relative group rounded-lg overflow-hidden" style={{ aspectRatio: "4/3", background: "#f3f4f6" }}>
                    <img src={src} alt={`Vehicle ${idx + 1}`} className="w-full h-full object-cover" />
                    {idx === 0 && (
                      <span className="absolute top-1 left-1 inline-flex items-center gap-1 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                        <Star size={10} /> MAIN
                      </span>
                    )}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                      {idx !== 0 && (
                        <button
                          type="button"
                          onClick={() => makeMain(idx)}
                          title="Set as main image"
                          className="bg-white text-gray-800 rounded-full p-1.5 hover:bg-gray-100"
                        >
                          <Star size={14} />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => removeImage(idx)}
                        title="Remove image"
                        className="bg-white text-red-600 rounded-full p-1.5 hover:bg-red-50"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={onPickFiles}
              style={{ display: "none" }}
            />
            <button type="button" className="photo-add" onClick={() => fileInputRef.current?.click()}>
              <Camera size={16} /> ADD PHOTO
            </button>
            {form.images.length > 1 && (
              <p className="text-xs muted mt-1">
                Hover a photo to reorder or delete. Click <Star size={11} className="inline" /> to set it as the main image.
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="field-label">Name *</label>
              <input className="input-soft" value={form.name} onChange={(e) => set("name", e.target.value)} required />
            </div>
            <div>
              <label className="field-label">Brand *</label>
              <input className="input-soft" value={form.brand} onChange={(e) => set("brand", e.target.value)} required />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="field-label">Model</label>
              <input className="input-soft" value={form.model} onChange={(e) => set("model", e.target.value)} />
            </div>
            <div>
              <label className="field-label">Year</label>
              <input type="number" className="input-soft" value={form.year} onChange={(e) => set("year", +e.target.value)} />
            </div>
            <div>
              <label className="field-label">Seats</label>
              <input type="number" className="input-soft" value={form.seats} onChange={(e) => set("seats", +e.target.value)} />
            </div>
            <div>
              <label className="field-label">Bags</label>
              <input type="number" className="input-soft" value={form.bags} onChange={(e) => set("bags", +e.target.value)} />
            </div>
          </div>

          <div>
            <label className="field-label">Category</label>
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => (
                <button type="button" key={c} className={`chip ${form.category === c ? "cat-active" : ""}`} onClick={() => set("category", c)}>{c}</button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="field-label">Price/day ($) *</label>
              <input type="number" className="input-soft" value={form.price} onChange={(e) => set("price", +e.target.value)} required />
            </div>
            <div>
              <label className="field-label">Transmission</label>
              <select className="input-soft" value={form.transmission} onChange={(e) => set("transmission", e.target.value)}>
                {transmissions.map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="field-label">Fuel</label>
              <select className="input-soft" value={form.fuel} onChange={(e) => set("fuel", e.target.value)}>
                {fuels.map((f) => <option key={f}>{f}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="field-label">Description</label>
            <textarea rows={3} className="input-soft" value={form.description} onChange={(e) => set("description", e.target.value)} />
          </div>

          <div>
            <label className="field-label">Mileage Policy</label>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.unlimitedMileage} onChange={(e) => set("unlimitedMileage", e.target.checked)} />
                Unlimited mileage
              </label>
              <input className="input-soft" placeholder="Daily mileage cap (km/day)" value={form.mileageCap} disabled={form.unlimitedMileage} onChange={(e) => set("mileageCap", e.target.value)} />
            </div>
            <p className="text-xs muted mt-1">Tick “Unlimited” OR enter a daily km cap (e.g., 200 km/day).</p>
          </div>

          <div>
            <label className="field-label">Driver Requirements</label>
            <label className="field-label" style={{ fontSize: "0.7rem" }}>Minimum driver age (years)</label>
            <input type="number" className="input-soft" value={form.minDriverAge} onChange={(e) => set("minDriverAge", +e.target.value)} />
          </div>

          <div>
            <label className="field-label">Vehicle Features</label>
            <div className="flex flex-wrap gap-2">
              {allFeatures.map((f) => (
                <button type="button" key={f} className={`chip ${form.features.includes(f) ? "cat-active" : ""}`} onClick={() => toggleFeature(f)}>{f}</button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="field-label">Pickup Location</label>
              <select className="input-soft" value={form.pickupLocation} onChange={(e) => set("pickupLocation", e.target.value)}>
                {locations.map((l) => <option key={l.id}>{l.name}</option>)}
              </select>
            </div>
            <div>
              <label className="field-label">Drop-off Location</label>
              <select className="input-soft" value={form.dropoffLocation} onChange={(e) => set("dropoffLocation", e.target.value)}>
                {locations.map((l) => <option key={l.id}>{l.name}</option>)}
              </select>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <button type="button" className="pill-btn outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="pill-btn primary">Save</button>
          </div>
        </form>
      </div>
    </div>
  );
}
